import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface RefreshContextType {
  registerRefreshHandler: (route: string, handler: () => Promise<void>) => void;
  unregisterRefreshHandler: (route: string) => void;
  triggerRefresh: (route: string) => Promise<void>;
  registerResetHandler: (handler: () => void) => void;
  resetTestPanel: () => void;
}

const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

export function RefreshProvider({ children }: { children: ReactNode }) {
  const [handlers, setHandlers] = useState<Map<string, () => Promise<void>>>(new Map());
  const [resetHandler, setResetHandler] = useState<(() => void) | null>(null);

  const registerRefreshHandler = useCallback((route: string, handler: () => Promise<void>) => {
    setHandlers(prev => {
      const updated = new Map(prev);
      updated.set(route, handler);
      return updated;
    });
  }, []);

  const unregisterRefreshHandler = useCallback((route: string) => {
    setHandlers(prev => {
      const updated = new Map(prev);
      updated.delete(route);
      return updated;
    });
  }, []);

  const triggerRefresh = useCallback(async (route: string) => {
    const handler = handlers.get(route);
    if (handler) {
      await handler();
    }
  }, [handlers]);

  const registerResetHandler = useCallback((handler: () => void) => {
    setResetHandler(() => handler);
  }, []);

  const resetTestPanel = useCallback(() => {
    if (resetHandler) {
      resetHandler();
    }
  }, [resetHandler]);

  return (
    <RefreshContext.Provider
      value={{
        registerRefreshHandler,
        unregisterRefreshHandler,
        triggerRefresh,
        registerResetHandler,
        resetTestPanel,
      }}
    >
      {children}
    </RefreshContext.Provider>
  );
}

export function useRefresh() {
  const context = useContext(RefreshContext);
  if (context === undefined) {
    throw new Error('useRefresh must be used within a RefreshProvider');
  }
  return context;
}
